import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Insignia, Tarjeta } from './ui/basicos';
import { usePermiso } from '../sesion/ProveedorDeSesion';

interface Requisito {
  readonly codigo: string;
  readonly estado: 'listo' | 'atencion' | 'falta';
  readonly ruta: string;
  readonly detalle?: readonly string[];
}

interface EstadoDePreparacion {
  readonly requisitos: readonly Requisito[];
  readonly puedeOperar: boolean;
}

const TONO = { listo: 'bien', atencion: 'atencion', falta: 'mal' } as const;

/**
 * Qué le falta al comercio para poder operar.
 *
 * **Cuando está todo bien es una sola línea**, no una lista de once tildes verdes. Una lista que
 * siempre está en verde deja de leerse, y el día que aparece un renglón rojo nadie lo ve. Lo que
 * falta se muestra entero; lo que está listo se resume.
 *
 * Es un componente y no una pantalla porque la misma pregunta se hace en dos momentos: al entrar
 * cualquier día —arriba del inicio— y al poner en marcha un comercio nuevo.
 */
export function Preparacion(): React.ReactElement | null {
  const { t } = useTranslation();
  const puedeVer = usePermiso('EMPRESA.CONFIGURACION.VER');

  const consulta = useQuery({
    queryKey: ['preparacion'],
    enabled: puedeVer,
    queryFn: () => pedir<EstadoDePreparacion>('/preparacion'),
  });

  // Quien no puede ver la configuración tampoco puede resolver nada de esto: mostrárselo sería
  // darle una tarea que no puede hacer. Una cajera no configura el plan de cuentas.
  if (!puedeVer || consulta.data === undefined) return null;

  const pendientes = consulta.data.requisitos.filter((uno) => uno.estado !== 'listo');

  if (pendientes.length === 0) {
    return (
      <Tarjeta>
        <p className="flex items-center gap-2 text-sm">
          <Insignia tono="bien">{t('preparacion.todoListo')}</Insignia>
          <span className="text-texto-tenue">{t('preparacion.todoListoAyuda')}</span>
        </p>
      </Tarjeta>
    );
  }

  return (
    <Tarjeta>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
        {t('preparacion.titulo')}
        {/* La distinción que hace útil a esta lista: `falta` es no poder operar, `atencion` es
            poder pero que va a doler. Mezclarlas la convierte en una lista de tareas que nadie
            termina. */}
        <Insignia tono={consulta.data.puedeOperar ? 'atencion' : 'mal'}>
          {consulta.data.puedeOperar
            ? t('preparacion.puedeOperar')
            : t('preparacion.noPuedeOperar')}
        </Insignia>
      </h2>

      <ul className="flex flex-col gap-2">
        {pendientes.map((requisito) => (
          <li key={requisito.codigo} className="flex flex-col gap-0.5 border-t border-borde pt-2">
            <p className="flex items-center gap-2 text-sm">
              <Insignia tono={TONO[requisito.estado]}>
                {t(`preparacion.estados.${requisito.estado}`)}
              </Insignia>
              <span>{t(`preparacion.requisitos.${requisito.codigo}`)}</span>
              {/* Un aviso sin destino es un aviso que molesta y no ayuda. */}
              <Link to={requisito.ruta} className="text-xs text-acento underline">
                {t('preparacion.resolver')}
              </Link>
            </p>
            {requisito.detalle === undefined ? null : (
              <p className="numero text-xs text-texto-tenue">
                {requisito.detalle.slice(0, 6).join(' · ')}
                {requisito.detalle.length > 6
                  ? ` … ${t('preparacion.yMas', { total: requisito.detalle.length - 6 })}`
                  : ''}
              </p>
            )}
          </li>
        ))}
      </ul>
    </Tarjeta>
  );
}
